/**
 * Persistence seam for `okra serve`.
 *
 * This slice moves document graphs, original PDF blobs, and leases behind the
 * store. Source refs and workflow/capability/client run records intentionally
 * remain under `OKRA_DATA_DIR`; their persistence is a later slice.
 */
import { type DocumentPersistence, type PgPoolLike } from '@okrapdf/core/persistence';
import { Effect } from 'effect';
import {
  existsSync,
  mkdirSync,
  mkdtempSync,
  readdirSync,
  readFileSync,
  rmSync,
  writeFileSync,
} from 'fs';
import { tmpdir } from 'os';
import { join, resolve } from 'path';
import {
  resolveEffectServeStore,
  ServeStoreError,
  type EffectServeStore,
} from './serve-store-effect.js';
import { isSelfHostResourceId } from './self-host-resource-id.js';

export interface StarterDocumentGraph {
  schema_version: 'okra-document-graph/v1';
  source: Record<string, unknown>;
  document: {
    id: string;
    source_id: string;
    sha256?: string;
    filename?: string;
    mime_type: string;
    lifecycle_state: 'ready';
    permissions: { access: 'private' };
  };
  pages: Array<Record<string, unknown>>;
  blocks: Array<Record<string, unknown>>;
  artifacts: Array<Record<string, unknown>>;
  findings: Array<Record<string, unknown>>;
  redactions: Array<Record<string, unknown>>;
  reviews: Array<Record<string, unknown>>;
  lineage: Array<Record<string, unknown>>;
}

export type MaterializedOriginal = {
  dataDir: string;
  documentId: string;
};

export interface ServeStore {
  readonly backend: 'filesystem' | 'postgres';
  ready(): Promise<void>;
  readGraph(documentId: string): Promise<StarterDocumentGraph | null>;
  writeGraph(graph: StarterDocumentGraph): Promise<StarterDocumentGraph>;
  listGraphs(): Promise<StarterDocumentGraph[]>;
  readOriginal(documentId: string): Promise<Uint8Array | null>;
  writeOriginal(documentId: string, bytes: Uint8Array): Promise<void>;
  withMaterializedOriginal<T>(
    documentId: string,
    use: (original: MaterializedOriginal) => T | Promise<T>,
  ): Promise<T>;
  acquireLease?(documentId: string, instanceId: string, ttlMs: number): Promise<boolean>;
  releaseLease?(documentId: string, instanceId: string): Promise<void>;
  close(): Promise<void>;
}

export type ServePgPoolFactory = (databaseUrl: string) => PgPoolLike | Promise<PgPoolLike>;

export type PersistenceServeStoreOptions = {
  ownerUserId?: string;
};

export type ResolveServeStoreOptions = {
  dataDir: string;
  env: Record<string, string | undefined>;
  poolFactory?: ServePgPoolFactory;
};

function ensureDir(path: string): void {
  mkdirSync(path, { recursive: true });
}

function graphPath(dataDir: string, documentId: string): string {
  if (!isSelfHostResourceId(documentId)) throw new Error('invalid self-host document id');
  return join(dataDir, 'documents', documentId, 'graph.json');
}

function originalPath(dataDir: string, documentId: string): string {
  if (!isSelfHostResourceId(documentId)) throw new Error('invalid self-host document id');
  return join(dataDir, 'documents', documentId, 'original.pdf');
}

function writeJson(path: string, value: unknown): void {
  ensureDir(resolve(path, '..'));
  writeFileSync(path, `${JSON.stringify(value, null, 2)}\n`);
}

function readJson(path: string): unknown {
  return JSON.parse(readFileSync(path, 'utf8')) as unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asGraph(value: unknown): StarterDocumentGraph | null {
  return isRecord(value) && value.schema_version === 'okra-document-graph/v1'
    ? (value as unknown as StarterDocumentGraph)
    : null;
}

export function createFilesystemServeStore(dataDir: string): ServeStore {
  return {
    backend: 'filesystem',
    async ready() {},
    async readGraph(documentId) {
      const path = graphPath(dataDir, documentId);
      return existsSync(path) ? asGraph(readJson(path)) : null;
    },
    async writeGraph(graph) {
      writeJson(graphPath(dataDir, graph.document.id), graph);
      return graph;
    },
    async listGraphs() {
      const dir = join(dataDir, 'documents');
      if (!existsSync(dir)) return [];
      const graphs = await Promise.all(
        readdirSync(dir, { withFileTypes: true })
          .filter((entry) => entry.isDirectory())
          .map((entry) => this.readGraph(entry.name)),
      );
      return graphs.filter((graph): graph is StarterDocumentGraph => graph !== null);
    },
    async readOriginal(documentId) {
      const path = originalPath(dataDir, documentId);
      return existsSync(path) ? new Uint8Array(readFileSync(path)) : null;
    },
    async writeOriginal(documentId, bytes) {
      const path = originalPath(dataDir, documentId);
      ensureDir(resolve(path, '..'));
      writeFileSync(path, bytes);
    },
    async withMaterializedOriginal(documentId, use) {
      return use({ dataDir, documentId });
    },
    async close() {},
  };
}

function emptyDocument(documentId: string, ownerUserId: string) {
  return {
    id: documentId,
    ownerUserId,
    orgId: null,
    projectId: null,
    isPublished: false,
    spec: {},
    meta: {},
    graph: {},
    phase: 'idle',
  };
}

export function createPersistenceServeStore(
  persistence: DocumentPersistence,
  options: PersistenceServeStoreOptions = {},
): ServeStore {
  const ownerUserId = options.ownerUserId?.trim() || 'self-host';

  const ensureDocument = async (documentId: string): Promise<void> => {
    if (!(await persistence.getDocument(documentId))) {
      await persistence.putDocument(emptyDocument(documentId, ownerUserId));
    }
  };

  return {
    backend: 'postgres',
    async ready() {},
    async readGraph(documentId) {
      return asGraph((await persistence.getDocument(documentId))?.graph);
    },
    async writeGraph(graph) {
      await persistence.putDocument({
        id: graph.document.id,
        ownerUserId,
        orgId: null,
        projectId: null,
        isPublished: false,
        spec: {},
        meta: {},
        graph: graph as unknown as Record<string, unknown>,
        phase: graph.document.lifecycle_state,
      });
      return graph;
    },
    async listGraphs() {
      const documents = await persistence.listDocumentsByOwner(ownerUserId);
      return documents.flatMap((document) => {
        const graph = asGraph(document.graph);
        return graph ? [graph] : [];
      });
    },
    async readOriginal(documentId) {
      const blob = await persistence.getBlob(documentId, 'source', 'source.pdf');
      return blob ? new Uint8Array(blob.bytes) : null;
    },
    async writeOriginal(documentId, bytes) {
      await ensureDocument(documentId);
      await persistence.putBlob({
        documentId,
        kind: 'source',
        name: 'source.pdf',
        contentType: 'application/pdf',
        bytes,
      });
    },
    async withMaterializedOriginal(documentId, use) {
      const materializedDataDir = mkdtempSync(join(tmpdir(), 'okrapdf-serve-original-'));
      const materializedDocumentId = 'document';
      try {
        const bytes = await this.readOriginal(documentId);
        if (bytes) {
          const path = originalPath(materializedDataDir, materializedDocumentId);
          ensureDir(resolve(path, '..'));
          writeFileSync(path, bytes);
        }
        return await use({ dataDir: materializedDataDir, documentId: materializedDocumentId });
      } finally {
        rmSync(materializedDataDir, { recursive: true, force: true });
      }
    },
    async acquireLease(documentId, instanceId, ttlMs) {
      await ensureDocument(documentId);
      return persistence.acquireLease(documentId, instanceId, ttlMs);
    },
    releaseLease: (documentId, instanceId) => persistence.releaseLease(documentId, instanceId),
    close: () => persistence.close(),
  };
}

async function runCompatibility<A>(effect: Effect.Effect<A, unknown>): Promise<A> {
  try {
    return await Effect.runPromise(effect);
  } catch (error) {
    if (error instanceof ServeStoreError && error.cause !== undefined) throw error.cause;
    throw error;
  }
}

/** Keep the released Promise contract as a thin adapter over the native store. */
export function toPromiseStore(store: EffectServeStore): ServeStore {
  return {
    backend: store.backend,
    ready: () => runCompatibility(store.ready()),
    readGraph: (documentId) => runCompatibility(store.readGraph(documentId)),
    writeGraph: (graph) => runCompatibility(store.writeGraph(graph)),
    listGraphs: async () => [...(await runCompatibility(store.listGraphs()))],
    readOriginal: (documentId) => runCompatibility(store.readOriginal(documentId)),
    writeOriginal: (documentId, bytes) => runCompatibility(store.writeOriginal(documentId, bytes)),
    withMaterializedOriginal: (documentId, use) =>
      runCompatibility(
        store.withMaterializedOriginal(documentId, (original) =>
          Effect.tryPromise({ try: () => Promise.resolve(use(original)), catch: (cause) => cause }),
        ),
      ),
    ...(store.acquireLease
      ? {
          acquireLease: (documentId: string, instanceId: string, ttlMs: number) =>
            runCompatibility(store.acquireLease!(documentId, instanceId, ttlMs)),
        }
      : {}),
    ...(store.releaseLease
      ? {
          releaseLease: (documentId: string, instanceId: string) =>
            runCompatibility(store.releaseLease!(documentId, instanceId)),
        }
      : {}),
    close: () => runCompatibility(store.close()),
  };
}

/** The only runtime decision between filesystem and Postgres persistence. */
export function resolveServeStore(options: ResolveServeStoreOptions): ServeStore {
  return toPromiseStore(resolveEffectServeStore(options));
}
